
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppSetting } from "./types";

export const useLegalSettings = () => {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [termsValue, setTermsValue] = useState("");
  const [privacyValue, setPrivacyValue] = useState("");

  // Fetch settings on hook initialization
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log("Fetching settings...");
      
      const { data, error } = await supabase
        .from("app_settings")
        .select("*");

      if (error) {
        throw error;
      }

      console.log("Fetched settings:", data);

      if (data) {
        setSettings(data as AppSetting[]);
        
        // Set initial values for terms and privacy
        const termsData = data.find(item => item.key === "terms_and_conditions");
        if (termsData) setTermsValue(termsData.value);
        
        const privacyData = data.find(item => item.key === "privacy_policy");
        if (privacyData) setPrivacyValue(privacyData.value);
        
        console.log("Terms data:", termsData);
        console.log("Privacy data:", privacyData);
      }
    } catch (error: any) {
      toast.error("Failed to fetch settings: " + error.message);
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (key: string, value: string) => {
    try {
      setSaving(true);
      console.log(`Saving settings for ${key}...`);
      console.log("Value to save:", value.substring(0, 50) + "...");
      
      // Check if the record exists first
      console.log("Checking if record exists...");
      const { data: existingData, error: existingError } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", key)
        .maybeSingle();
      
      console.log("Existing data check result:", existingData);
      
      if (existingError) {
        console.error("Error checking for existing record:", existingError);
        throw existingError;
      }

      let result;
      
      // Call the new RPC function to update settings as admin
      // This bypasses RLS policies since it's a server-side function
      console.log("Using admin function to update settings...");
      const { data, error } = await supabase.rpc(
        'admin_update_app_setting' as any, 
        { 
          p_key: key, 
          p_value: value,
          p_description: existingData?.description || (key === "terms_and_conditions" ? 
            "Terms and Conditions content" : "Privacy Policy content")
        }
      );

      console.log("Admin update response:", data);

      if (error) {
        console.error("Error updating/inserting record:", error);
        throw error;
      }
      
      result = data;

      toast.success(`${key.replace(/_/g, " ")} successfully updated`);
      console.log("Save successful, refreshing settings...");
      
      // Refresh settings to get updated values
      await fetchSettings();
      
      return result;
    } catch (error: any) {
      toast.error("Failed to update settings: " + error.message);
      console.error("Error saving settings:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTerms = () => {
    return saveSettings("terms_and_conditions", termsValue);
  };

  const handleSavePrivacy = () => {
    return saveSettings("privacy_policy", privacyValue);
  };

  const getLastUpdatedText = (key: string) => {
    const setting = settings.find(item => item.key === key);
    if (!setting) return "";
    
    const updatedAt = new Date(setting.updated_at);
    return `Last updated: ${updatedAt.toLocaleDateString()} ${updatedAt.toLocaleTimeString()}`;
  };

  return { 
    settings,
    loading,
    saving,
    termsValue,
    setTermsValue,
    privacyValue,
    setPrivacyValue,
    fetchSettings,
    saveSettings,
    handleSaveTerms,
    handleSavePrivacy,
    getLastUpdatedText
  };
};
